<template>
    <transition name="KbHelpPanel">
        <aside
            v-if="openArticleId"
            class="KbHelpPanel"
            role="complementary"
            aria-label="Help"
        >
            <header class="KbHelpPanel__header">
                <KbBreadcrumbs class="KbHelpPanel__eyebrow" :sections="sectionPath" compact />
                <div class="KbHelpPanel__actions">
                    <NewButton
                        size="icon-xs"
                        variant="flat"
                        color="neutral"
                        class="KbHelpPanel__expand"
                        aria-label="Open full page"
                        @click="openFullPage"
                    >
                        <EidIcon :icon-src="faUpRightAndDownLeftFromCenter" />
                    </NewButton>
                    <NewButton
                        ref="closeButton"
                        size="icon-xs"
                        variant="flat"
                        color="neutral"
                        class="KbHelpPanel__close"
                        aria-label="Close help"
                        @click="close"
                    >
                        <EidIcon :icon-src="faXmark" />
                    </NewButton>
                </div>
            </header>
            <div class="KbHelpPanel__body">
                <h2 v-if="article" class="KbHelpPanel__title">
                    {{ article.title }}
                </h2>
                <div v-if="isLoading" class="KbHelpPanel__loading">
                    Loading…
                </div>
                <div v-else-if="hasError" class="KbHelpPanel__error">
                    This article is not available right now.
                </div>
                <KbArticleContent
                    v-else-if="article"
                    :article="article"
                    frame="panel"
                    @navigate="navigate"
                />
            </div>
        </aside>
    </transition>
</template>

<style lang="scss">
/* Docked to the right and bounded to a fixed max width -- never a full-viewport
   layer -- so there is nothing here that can cover the page the way a bare
   `.Modal` class does elsewhere in this app. Closing removes the node from the
   DOM (via v-if) rather than hiding it in place, so a stale copy cannot linger
   and intercept clicks. */

.KbHelpPanel {
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    z-index: 9990;
    display: flex;
    flex-direction: column;
    width: min(420px, 100vw);
    border-left: 1px solid var(--eidTechGray-25);
    background: var(--eidWhite);
    box-shadow: -8px 0 24px rgba(0, 0, 0, 0.08);

    &__header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        flex: 0 0 auto;
        padding: 0.75rem 1rem;
        border-bottom: 1px solid var(--eidTechGray-25);
    }

    &__eyebrow {
        min-width: 0;
        flex: 1;
        color: var(--eidTechGray-75);
        font-size: 12px;
        font-weight: 400;
    }

    &__actions {
        display: flex;
        align-items: center;
        gap: 0.25rem;
    }

    &__body {
        flex: 1 1 auto;
        padding: 1rem;
        overflow-y: auto;
    }

    &__title {
        margin: 0 0 0.75rem;
        color: var(--eidSecureBlue);
        font-size: 18px;
        font-weight: 700;
    }

    &__loading,
    &__error {
        color: var(--eidTechGray-75);
        font-size: 14px;
    }

    &-enter-active,
    &-leave-active {
        transition: transform 0.2s ease;
    }

    &-enter-from,
    &-leave-to {
        transform: translateX(100%);
    }
}

/* The panel cannot sit beside the content on a phone, so it takes the screen. */
@media (max-width: 640px) {
    .KbHelpPanel {
        width: 100vw;
    }
}
</style>

<script lang="ts">
    import { defineComponent } from 'vue';

    import { faUpRightAndDownLeftFromCenter, faXmark } from '@fortawesome/pro-light-svg-icons';

    import EidIcon from '@evidentid/dashboard-commons/components/EidIcon/EidIcon.vue';
    import { KbArticle, KbSection } from '@evidentid/tprm-portal-lib/models/kb';
    import { OperationStatus } from '@evidentid/vue-commons/store/OperationStatus';

    import NewButton from '@/components/new-button/NewButton.vue';
    import { currentRpId, KbNavigationTarget, routeForTarget } from '@/modules/kb/components/KbArticleContent/helpers';
    import KbArticleContent from '@/modules/kb/components/KbArticleContent/KbArticleContent.vue';
    import KbBreadcrumbs from '@/modules/kb/components/KbBreadcrumbs/KbBreadcrumbs.vue';
    import { useKbStore } from '@/modules/kb/store';
    import { findSectionPath } from '@/modules/kb/utils/sectionPath';

    export default defineComponent({
        name: 'KbHelpPanel',
        components: { EidIcon, KbArticleContent, KbBreadcrumbs, NewButton },
        data: () => ({
            faUpRightAndDownLeftFromCenter,
            faXmark,
            // The element focused right before the panel opened, so closing can hand
            // focus back to it instead of dropping it to the document body.
            elementFocusedBeforeOpen: null as HTMLElement | null,
        }),
        computed: {
            kbStore() {
                return useKbStore();
            },
            // The open article lives in the URL rather than in component state, so the
            // panel is shareable, survives a refresh, and closes on the browser Back
            // button instead of stranding the reader a page away from their work.
            openArticleId(): string | null {
                const value = this.$route.query.kb;
                return typeof value === 'string' && value ? value : null;
            },
            // The heading the reader's link pointed at inside the open article, if any.
            openFragment(): string | null {
                const value = this.$route.query.kbAnchor;
                return typeof value === 'string' && value ? value : null;
            },
            article(): KbArticle | null {
                return this.openArticleId ? this.kbStore.articles[this.openArticleId] || null : null;
            },
            sectionPath(): KbSection[] {
                return this.article ? findSectionPath(this.kbStore.tree, this.article.sectionId) : [];
            },
            isLoading(): boolean {
                if (!this.openArticleId) {
                    return false;
                }
                const status = this.kbStore.articleStatus[this.openArticleId];
                return status === undefined || status === OperationStatus.loading;
            },
            hasError(): boolean {
                return Boolean(this.openArticleId)
                    && this.kbStore.articleStatus[this.openArticleId as string] === OperationStatus.error;
            },
        },
        watch: {
            openArticleId: {
                immediate: true,
                handler(id: string | null, previousId?: string | null): void {
                    if (id) {
                        this.kbStore.loadArticle(id);
                        this.kbStore.loadTree();
                    }
                    if (id && !previousId) {
                        this.captureFocus();
                    } else if (!id && previousId) {
                        this.restoreFocus();
                    }
                },
            },
        },
        beforeUnmount(): void {
            // Guards against the panel's host being torn down (e.g. a navigation away
            // from this whole app section) while it was still open, so focus is never
            // left on a control that no longer exists.
            if (this.openArticleId) {
                this.restoreFocus();
            }
        },
        methods: {
            captureFocus(): void {
                this.elementFocusedBeforeOpen = document.activeElement as HTMLElement;
                this.$nextTick(() => {
                    const closeButton = this.$refs.closeButton as { $el?: HTMLElement } | undefined;
                    closeButton?.$el?.focus?.();
                });
            },
            restoreFocus(): void {
                this.elementFocusedBeforeOpen?.focus?.();
                this.elementFocusedBeforeOpen = null;
            },
            close(): void {
                if (!this.openArticleId) {
                    return;
                }
                const query = { ...this.$route.query };
                delete query.kb;
                delete query.kbAnchor;
                this.$router.push({ ...this.$route, query });
            },
            // An article link swaps the article inside the panel, as before. A section or the
            // help home is a full page: go there without the `kb` query, which closes the panel.
            navigate(target: KbNavigationTarget): void {
                if (target.kind === 'article') {
                    this.showArticle(target.id, target.fragment);
                    return;
                }
                this.$router.push(routeForTarget(target, currentRpId(this.$route)));
            },
            showArticle(id: string, fragment?: string): void {
                // A reader clicking the same in-panel link twice, or a caller opening an
                // already-open article again, must not push a second, identical history
                // entry -- that would make Back close the panel twice.
                if (id === this.openArticleId && (fragment || null) === this.openFragment) {
                    return;
                }
                const query = { ...this.$route.query, kb: id };
                delete query.kbAnchor;
                this.$router.push({ ...this.$route, query: fragment ? { ...query, kbAnchor: fragment } : query });
            },
            openFullPage(): void {
                if (!this.openArticleId) {
                    return;
                }
                this.$router.push(routeForTarget(
                    { kind: 'article', id: this.openArticleId, ...(this.openFragment ? { fragment: this.openFragment } : {}) },
                    currentRpId(this.$route),
                ));
            },
        },
    });
</script>
